// src/pages/ActiveDirectoryAuditDetails.jsx - AD Audit Detail, Analysis & CISO Summary
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Grid, Alert, CircularProgress,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Tabs, Tab, Divider, FormControl, InputLabel, Select, MenuItem, Accordion,
  AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Psychology as AnalyzeIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Compare as CompareIcon,
  Block as IgnoreIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import VoiceInput from '../components/VoiceInput';
import { adAuditApi } from '../services/api';
import { toast } from 'react-toastify';

const SEV_COLORS = {
  Critical: '#e74c3c',
  High: '#e67e22',
  Medium: '#f39c12',
  Low: '#3498db',
  Info: '#95a5a6'
};

const ActiveDirectoryAuditDetails = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [cisoSummary, setCisoSummary] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [severityFilter, setSeverityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [includeIgnored, setIncludeIgnored] = useState(false);
  const [ignoreDialog, setIgnoreDialog] = useState({ open: false, finding: null });
  const [ignoreReason, setIgnoreReason] = useState('');
  const ignoreReasonRef = useRef(null);
  const cisoSummaryRef = useRef(null);

  useEffect(() => {
    loadAudit();
    loadFindings();
  }, [auditId, severityFilter, categoryFilter, includeIgnored]);

  const loadAudit = async () => {
    try {
      setLoading(true);
      const res = await adAuditApi.getAudit(auditId);
      setAudit(res.data);
      if (res.data.analysis) {
        setCisoSummary(res.data.analysis.ciso_summary || '');
      }
    } catch {
      toast.error('Failed to load audit');
      navigate('/active-directory-audits');
    } finally {
      setLoading(false);
    }
  };

  const loadFindings = async () => {
    try {
      const params = {
        severity: severityFilter || undefined,
        category: categoryFilter || undefined,
        include_ignored: includeIgnored
      };
      const res = await adAuditApi.getFindings(auditId, params);
      setFindings(res.data);
    } catch (err) {
      console.error('Failed to load findings:', err);
    }
  };

  const handleAnalyze = async () => {
    if (!window.confirm(
      'This will send your uploaded documents to the Claude AI API for analysis. ' +
      'All findings will be extracted and categorized. Continue?'
    )) return;

    const applyAnalysisResult = async (data) => {
      setAudit(data);
      if (data.analysis) setCisoSummary(data.analysis.ciso_summary || '');
      await loadFindings();
      setCurrentTab(0);
      toast.success(`Analysis complete! ${data.total_findings} findings extracted.`);
    };

    let pollingActive = false;
    try {
      setAnalyzing(true);
      const res = await adAuditApi.analyzeAudit(auditId);
      const { total_findings, severity_counts, risk_score, analysis } = res.data;

      // Update the audit record in state directly — no page reload needed
      setAudit(prev => ({
        ...prev,
        status: 'analyzed',
        total_findings,
        critical_count: severity_counts.Critical,
        high_count: severity_counts.High,
        medium_count: severity_counts.Medium,
        low_count: severity_counts.Low,
        info_count: severity_counts.Info,
        overall_score: risk_score,
        analysis: {
          ciso_summary: analysis.ciso_summary,
          ciso_summary_edited: false,
          key_findings: JSON.stringify(analysis.key_findings || []),
          risk_by_category: JSON.stringify(analysis.risk_by_category || []),
          remediation_recommendations: JSON.stringify(analysis.remediation_recommendations || []),
          compliance_summary: JSON.stringify(analysis.compliance_summary || {})
        }
      }));
      setCisoSummary(analysis.ciso_summary || '');
      await loadFindings();
      setCurrentTab(0);
      toast.success(`Analysis complete! ${total_findings} findings extracted.`);
    } catch (err) {
      // The request may have timed out at the network/proxy layer while the backend
      // is still processing. Keep the spinner going and poll until analysis finishes.
      pollingActive = true;
      let attempts = 0;
      const maxAttempts = 30; // poll every 5s for up to 2.5 minutes

      const poll = async () => {
        try {
          const checkRes = await adAuditApi.getAudit(auditId);
          if (checkRes.data.status === 'analyzed' || checkRes.data.status === 'report_generated') {
            await applyAnalysisResult(checkRes.data);
            setAnalyzing(false);
            return;
          }
          if (checkRes.data.status === 'error') {
            toast.error('Analysis failed on the server. Please try again.');
            setAnalyzing(false);
            return;
          }
        } catch (_) { /* network hiccup — keep polling */ }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          toast.error('Analysis is taking longer than expected. Please refresh the page.');
          setAnalyzing(false);
        }
      };

      poll(); // start polling — spinner stays on until poll() calls setAnalyzing(false)
    } finally {
      if (!pollingActive) setAnalyzing(false);
    }
  };

  const handleSaveSummary = async () => {
    try {
      await adAuditApi.updateCisoSummary(auditId, cisoSummary);
      // Update in state directly so the "Edited" chip appears without a page reload
      setAudit(prev => ({
        ...prev,
        analysis: { ...prev.analysis, ciso_summary: cisoSummary, ciso_summary_edited: true }
      }));
      toast.success('CISO summary updated');
      setEditingSummary(false);
    } catch {
      toast.error('Failed to update summary');
    }
  };

  const handleGenerateReport = async () => {
    try {
      setGenerating(true);
      await adAuditApi.generateReport(auditId);
      toast.success('Report downloaded successfully!');
      loadAudit();
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleIgnoreSubmit = async () => {
    if (!ignoreReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    try {
      await adAuditApi.ignoreFinding(ignoreDialog.finding.id, ignoreReason);
      toast.success('Finding ignored');
      setIgnoreDialog({ open: false, finding: null });
      setIgnoreReason('');
      loadFindings();
    } catch {
      toast.error('Failed to ignore finding');
    }
  };

  const handleUnignore = async (findingId) => {
    if (!window.confirm('Remove ignored status from this finding?')) return;
    try {
      await adAuditApi.unignoreFinding(findingId);
      toast.success('Finding un-ignored');
      loadFindings();
    } catch {
      toast.error('Failed to un-ignore finding');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#6B3FA0' }} />
      </Box>
    );
  }

  if (!audit) return null;

  const analysis = audit.analysis || {};
  const keyFindings = analysis.key_findings ? JSON.parse(analysis.key_findings) : [];
  const riskByCategory = analysis.risk_by_category ? JSON.parse(analysis.risk_by_category) : [];
  const recommendations = analysis.remediation_recommendations ? JSON.parse(analysis.remediation_recommendations) : [];
  const compliance = analysis.compliance_summary ? JSON.parse(analysis.compliance_summary) : {};

  const categories = [...new Set(findings.map(f => f.category).filter(Boolean))].sort();

  const getRiskColor = (score) => {
    if (score >= 60) return '#e74c3c';
    if (score >= 30) return '#e67e22';
    return '#27ae60';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/active-directory-audits')}
          sx={{ mb: 2, color: '#6B3FA0' }}
        >
          Back to Audits
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50' }}>
              {audit.audit_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {audit.client_name} • {audit.audit_date ? new Date(audit.audit_date).toLocaleDateString() : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(() => {
                try {
                  const fns = JSON.parse(audit.uploaded_filenames || '[]');
                  return fns.join(', ');
                } catch { return audit.uploaded_filenames || ''; }
              })()}
            </Typography>
          </div>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {audit.status === 'processing' || audit.status === 'error' ? (
              <Button
                variant="contained"
                startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <AnalyzeIcon />}
                onClick={handleAnalyze}
                disabled={analyzing}
                sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
              >
                {analyzing ? 'Analyzing...' : 'Analyze with AI'}
              </Button>
            ) : null}
            {(audit.status === 'analyzed' || audit.status === 'report_generated') && (
              <>
                <Button
                  variant="outlined"
                  startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <AnalyzeIcon />}
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  sx={{ borderColor: '#6B3FA0', color: '#6B3FA0' }}
                >
                  Re-analyze
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CompareIcon />}
                  onClick={() => navigate(`/active-directory-audits/${auditId}/compare`)}
                  sx={{ borderColor: '#6B3FA0', color: '#6B3FA0' }}
                >
                  Compare
                </Button>
                <Button
                  variant="contained"
                  startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={handleGenerateReport}
                  disabled={generating}
                  sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#229954' } }}
                >
                  {generating ? 'Generating...' : 'Generate Report'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Findings', value: audit.total_findings || 0, bg: '#ecf0f1', color: '#2c3e50' },
          { label: 'Critical', value: audit.critical_count || 0, bg: '#fadbd8', color: '#e74c3c' },
          { label: 'High', value: audit.high_count || 0, bg: '#fdebd0', color: '#e67e22' },
          { label: 'Medium', value: audit.medium_count || 0, bg: '#fcf3cf', color: '#f39c12' },
          { label: 'Low', value: audit.low_count || 0, bg: '#d6eaf8', color: '#3498db' },
        ].map(({ label, value, bg, color }) => (
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={label}>
            <Card sx={{ textAlign: 'center', bgcolor: bg }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card sx={{ textAlign: 'center', bgcolor: '#eaf5ea' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: getRiskColor(parseFloat(audit.overall_score || 0)) }}
              >
                {parseFloat(audit.overall_score || 0).toFixed(1)}/100
              </Typography>
              <Typography variant="body2" color="text.secondary">Risk Score</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, v) => setCurrentTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          textColor="inherit"
          TabIndicatorProps={{ style: { backgroundColor: '#6B3FA0' } }}
        >
          <Tab label="Overview" />
          <Tab label={`Findings (${findings.filter(f => !f.is_ignored).length})`} />
          <Tab label="Analysis" disabled={!analysis.ciso_summary} />
          <Tab label="Compliance" disabled={!compliance.overall_compliance_risk} />
        </Tabs>
      </Card>

      {/* === OVERVIEW TAB === */}
      {currentTab === 0 && (
        <Grid container spacing={3}>
          {/* CISO Summary */}
          {analysis.ciso_summary && (
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#4A2B70' }}>
                      CISO Executive Summary
                      {Boolean(analysis.ciso_summary_edited) && (
                        <Chip label="Edited" size="small" color="primary" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {editingSummary ? (
                        <>
                          <Button
                            startIcon={<SaveIcon />}
                            onClick={handleSaveSummary}
                            variant="contained"
                            size="small"
                            sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
                          >
                            Save
                          </Button>
                          <Button size="small" onClick={() => setEditingSummary(false)}>Cancel</Button>
                        </>
                      ) : (
                        <Button
                          startIcon={<EditIcon />}
                          onClick={() => setEditingSummary(true)}
                          size="small"
                          sx={{ color: '#6B3FA0' }}
                        >
                          Edit
                        </Button>
                      )}
                    </Box>
                  </Box>
                  {editingSummary ? (
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={14}
                        value={cisoSummary}
                        onChange={(e) => setCisoSummary(e.target.value)}
                        inputRef={cisoSummaryRef}
                      />
                      <Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
                        <VoiceInput
                          onTranscript={(text) => setCisoSummary(text)}
                          currentValue={cisoSummary}
                          inputRef={cisoSummaryRef}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                      {cisoSummary}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* No analysis yet */}
          {!analysis.ciso_summary && (
            <Grid size={12}>
              <Card sx={{ border: '2px dashed #E5D5F5' }}>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <SecurityIcon sx={{ fontSize: 64, color: '#E5D5F5', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No Analysis Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Click "Analyze with AI" to have Claude extract findings and generate the CISO Executive Summary.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <AnalyzeIcon />}
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
                  >
                    {analyzing ? 'Analyzing...' : 'Analyze with AI'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Key Findings */}
          {keyFindings.length > 0 && (
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#4A2B70' }}>
                    Key Findings
                  </Typography>
                  {keyFindings.map((kf, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip
                          label={kf.severity || 'N/A'}
                          size="small"
                          sx={{
                            bgcolor: SEV_COLORS[kf.severity] || '#95a5a6',
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {idx + 1}. {kf.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 0.5, ml: 1 }}>{kf.description}</Typography>
                      {kf.impact && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          Impact: {kf.impact}
                        </Typography>
                      )}
                      {idx < keyFindings.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Risk by Category */}
          {riskByCategory.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#4A2B70' }}>
                    Risk by Category
                  </Typography>
                  {riskByCategory.map((cat, idx) => (
                    <Box key={idx} sx={{ mb: 2, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{cat.category}</Typography>
                        <Chip
                          label={cat.risk_level}
                          size="small"
                          sx={{
                            bgcolor: SEV_COLORS[cat.risk_level] || '#95a5a6',
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {cat.finding_count} finding{cat.finding_count !== 1 ? 's' : ''} • {cat.summary}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* === FINDINGS TAB === */}
      {currentTab === 1 && (
        <Box>
          {/* Filters */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  label="Severity"
                >
                  <MenuItem value="">All Severities</MenuItem>
                  {['Critical', 'High', 'Medium', 'Low', 'Info'].map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map(c => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                startIcon={includeIgnored ? <HideIcon /> : <ViewIcon />}
                onClick={() => setIncludeIgnored(!includeIgnored)}
                variant="outlined"
                sx={{ borderColor: '#6B3FA0', color: '#6B3FA0' }}
              >
                {includeIgnored ? 'Hide' : 'Show'} Ignored
              </Button>
            </CardContent>
          </Card>

          {findings.length === 0 ? (
            <Alert severity="info">No findings match the current filters.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#ecf0f1' }}>
                  <TableRow>
                    <TableCell><strong>Severity</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell><strong>Finding</strong></TableCell>
                    <TableCell><strong>Affected Objects</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {findings.map((finding) => (
                    <TableRow key={finding.id} sx={{ opacity: finding.is_ignored ? 0.5 : 1 }}>
                      <TableCell>
                        <Chip
                          label={finding.severity}
                          size="small"
                          sx={{
                            bgcolor: SEV_COLORS[finding.severity] || '#95a5a6',
                            color: 'white',
                            fontWeight: 700,
                            minWidth: 70
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={finding.category || 'Other'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {finding.finding_name}
                        </Typography>
                        {finding.description && (
                          <Typography variant="caption" color="text.secondary">
                            {finding.description.substring(0, 120)}
                            {finding.description.length > 120 ? '...' : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="caption">
                          {finding.affected_objects
                            ? finding.affected_objects.substring(0, 100) + (finding.affected_objects.length > 100 ? '...' : '')
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {finding.is_ignored ? (
                          <Chip label="Ignored" size="small" color="default" />
                        ) : (
                          <Chip label="Active" size="small" color="success" />
                        )}
                      </TableCell>
                      <TableCell>
                        {finding.is_ignored ? (
                          <IconButton size="small" onClick={() => handleUnignore(finding.id)} title="Un-ignore">
                            <ViewIcon />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => { setIgnoreDialog({ open: true, finding }); setIgnoreReason(''); }}
                            title="Ignore"
                          >
                            <IgnoreIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* === ANALYSIS TAB === */}
      {currentTab === 2 && (
        <Grid container spacing={3}>
          {recommendations.length > 0 && (
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#4A2B70' }}>
                    Remediation Recommendations
                  </Typography>
                  {recommendations.map((rec, idx) => (
                    <Accordion key={idx} sx={{ mb: 1, border: '1px solid #E5D5F5' }} elevation={0}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                          <Chip
                            label={`#${rec.priority}`}
                            size="small"
                            sx={{ bgcolor: '#6B3FA0', color: 'white', minWidth: 40 }}
                          />
                          <Typography sx={{ fontWeight: 600, flex: 1 }}>{rec.action}</Typography>
                          <Chip label={rec.timeframe} size="small" color="primary" variant="outlined" />
                          <Chip
                            label={rec.estimated_effort}
                            size="small"
                            color={rec.estimated_effort === 'High' ? 'error' : rec.estimated_effort === 'Medium' ? 'warning' : 'success'}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2">{rec.rationale}</Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {riskByCategory.length > 0 && (
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#4A2B70' }}>
                    Risk by Category
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#F3EEFA' }}>
                        <TableRow>
                          <TableCell><strong>Category</strong></TableCell>
                          <TableCell align="center"><strong>Risk Level</strong></TableCell>
                          <TableCell align="center"><strong>Findings</strong></TableCell>
                          <TableCell><strong>Summary</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {riskByCategory.map((cat, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontWeight: 600 }}>{cat.category}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={cat.risk_level}
                                size="small"
                                sx={{
                                  bgcolor: SEV_COLORS[cat.risk_level] || '#95a5a6',
                                  color: 'white',
                                  fontWeight: 700
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">{cat.finding_count}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{cat.summary}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* === COMPLIANCE TAB === */}
      {currentTab === 3 && (
        <Grid container spacing={3}>
          <Grid size={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <WarningIcon sx={{ color: '#e74c3c', fontSize: 32 }} />
                  <div>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#4A2B70' }}>
                      Compliance Summary
                    </Typography>
                    {compliance.overall_compliance_risk && (
                      <Chip
                        label={`Overall Risk: ${compliance.overall_compliance_risk}`}
                        sx={{
                          bgcolor: SEV_COLORS[compliance.overall_compliance_risk] || '#95a5a6',
                          color: 'white',
                          fontWeight: 700
                        }}
                      />
                    )}
                  </div>
                </Box>

                {compliance.hipaa_violations?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#e74c3c', mb: 1 }}>
                      HIPAA Violations ({compliance.hipaa_violations.length})
                    </Typography>
                    {compliance.hipaa_violations.map((v, i) => (
                      <Alert key={i} severity="error" sx={{ mb: 1 }}>{v}</Alert>
                    ))}
                  </Box>
                )}

                {compliance.nist_gaps?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#e67e22', mb: 1 }}>
                      NIST Framework Gaps ({compliance.nist_gaps.length})
                    </Typography>
                    {compliance.nist_gaps.map((g, i) => (
                      <Alert key={i} severity="warning" sx={{ mb: 1 }}>{g}</Alert>
                    ))}
                  </Box>
                )}

                {compliance.other_compliance?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#3498db', mb: 1 }}>
                      Other Compliance Issues ({compliance.other_compliance.length})
                    </Typography>
                    {compliance.other_compliance.map((c, i) => (
                      <Alert key={i} severity="info" sx={{ mb: 1 }}>{c}</Alert>
                    ))}
                  </Box>
                )}

                {!compliance.hipaa_violations?.length && !compliance.nist_gaps?.length && !compliance.other_compliance?.length && (
                  <Alert severity="success">No compliance violations identified.</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Ignore Dialog */}
      <Dialog
        open={ignoreDialog.open}
        onClose={() => setIgnoreDialog({ open: false, finding: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ignore Finding</DialogTitle>
        <DialogContent>
          {ignoreDialog.finding && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Ignoring: <strong>{ignoreDialog.finding.finding_name}</strong>
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            This finding will be excluded from reports and risk scoring for this audit only.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for Ignoring (Required)"
            value={ignoreReason}
            onChange={(e) => setIgnoreReason(e.target.value)}
            placeholder="e.g. False positive, accepted risk, compensating controls in place..."
            inputRef={ignoreReasonRef}
            InputProps={{
              endAdornment: (
                <VoiceInput
                  onTranscript={(text) => setIgnoreReason(text)}
                  currentValue={ignoreReason}
                  inputRef={ignoreReasonRef}
                />
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIgnoreDialog({ open: false, finding: null })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleIgnoreSubmit}
            disabled={!ignoreReason.trim()}
            sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
          >
            Ignore Finding
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActiveDirectoryAuditDetails;
